import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/auth/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  HelpCircle,
  MessageSquare,
  ChevronRight,
  Send,
  Ticket,
  Shield,
  Bot,
  User,
  Square,
  SquarePen,
  X,
  TicketPlus,
} from 'lucide-react';
import { useTickets } from '../hooks';
import { useFaq } from '../hooks';
import api from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { cn } from '@/lib/utils';
import { formatMessageContent } from '@/lib/format-error';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { ARCHE_ID, ARCHE_PATH } from '../constants';

const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
const MAX_CONVERSATION_MESSAGES = 20;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
}

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hello! I'm your AI support assistant. I can help you with questions about the Fias platform, troubleshoot issues, or guide you through features. What can I help you with?",
};

export function SupportHomePage() {
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);
  const { tickets, loading: ticketsLoading, refresh: refreshTickets } = useTickets();
  const { faqs, loading: faqsLoading } = useFaq();
  const navigate = useNavigate();

  // Ticket dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [heroInput, setHeroInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [isStreaming, setIsStreaming] = useState(false);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // WebSocket streaming
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);
  const authToken = authApi.getIdToken();

  const {
    streamingText,
    isComplete: streamingComplete,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!currentRequestId,
  });

  const combinedStreamingText = useMemo(() => {
    return Object.values(streamingText).join('');
  }, [streamingText]);

  const recentTickets = tickets.slice(0, 5);
  const topFaqs = faqs.slice(0, 3);

  // Scroll chat to bottom (scoped to chat container only)
  const scrollChatToBottom = useCallback(() => {
    const container = chatScrollRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Stream via WebSocket
  const streamWithWebSocket = useCallback(
    async (
      assistantMessageId: string,
      conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
    ) => {
      try {
        const result = await api.startArche(ARCHE_ID, {
          messages: conversationHistory,
          provider: 'openai',
        });
        setCurrentRequestId(result.requestId);
        setCurrentAssistantMessageId(assistantMessageId);
      } catch (err) {
        console.error('Streaming failed:', err);
        const errorStr = String(err).toLowerCase();
        const isAuthError = errorStr.includes('unauthorized') || errorStr.includes('token');
        const errorMessage = isAuthError
          ? 'Your session has expired. Please log out and log back in to continue.'
          : 'Sorry, something went wrong. Please try again.';

        setChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: errorMessage, isStreaming: false }
              : msg,
          ),
        );
        setIsStreaming(false);
      }
    },
    [],
  );

  // Submit a chat message
  const submitChatMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming) return;

      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      const userMessage: ChatMessage = { id: userMessageId, role: 'user', content: text.trim() };
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      };

      setChatMessages((prev) => [...prev, userMessage, assistantMessage]);
      setChatInput('');
      setIsStreaming(true);
      setTimeout(() => scrollChatToBottom(), 50);

      const allMessages = [...chatMessages, userMessage].filter(
        (msg) => msg.content.trim().length > 0 && msg.id !== 'welcome',
      );
      const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      const conversationHistory = truncatedMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      await streamWithWebSocket(assistantMessageId, conversationHistory);
    },
    [isStreaming, chatMessages, scrollChatToBottom, streamWithWebSocket],
  );

  // Update assistant message with streaming text
  useEffect(() => {
    if (currentAssistantMessageId && combinedStreamingText) {
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, content: combinedStreamingText } : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText]);

  // Handle streaming completion
  useEffect(() => {
    if (streamingComplete && currentAssistantMessageId) {
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingComplete, currentAssistantMessageId, resetStreaming]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantMessageId) {
      const isAuthError =
        streamingError.message.toLowerCase().includes('unauthorized') ||
        streamingError.code === 'UNAUTHORIZED';
      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : `Error: ${streamingError.message}`;

      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantMessageId, resetStreaming]);

  // Auto-scroll on message changes
  useEffect(() => {
    if (chatOpen) {
      scrollChatToBottom();
    }
  }, [chatMessages, chatOpen, scrollChatToBottom]);

  // Auto-resize chat textarea
  useEffect(() => {
    const textarea = chatTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [chatInput]);

  // Hero input submit — opens chat and sends message
  const handleHeroSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!heroInput.trim()) return;
    const query = heroInput.trim();
    setHeroInput('');
    setChatOpen(true);
    // Small delay to let the chat mount, then submit
    setTimeout(() => submitChatMessage(query), 100);
  };

  // Chat input submit
  const handleChatSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    submitChatMessage(chatInput);
  };

  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSubmit();
    }
  };

  const handleStop = useCallback(() => {
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    setIsStreaming(false);
    setChatMessages((prev) =>
      prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)),
    );
  }, [resetStreaming]);

  const handleResetChat = useCallback(() => {
    setChatMessages([WELCOME_MESSAGE]);
    setChatInput('');
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    setIsStreaming(false);
    chatTextareaRef.current?.focus();
  }, [resetStreaming]);

  const handleCloseChat = useCallback(() => {
    if (isStreaming) return;
    setChatOpen(false);
    handleResetChat();
  }, [isStreaming, handleResetChat]);

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;

    setSubmitting(true);
    setTicketError(null);
    try {
      const ticket = await api.createSupportTicket({
        subject: subject.trim(),
        description: description.trim(),
      });
      setSubject('');
      setDescription('');
      setDialogOpen(false);
      toast({ title: 'Ticket created' });
      await refreshTickets();
      navigate(`${ARCHE_PATH}/tickets/${ticket.ticketId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create ticket';
      setTicketError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Count user messages (excluding welcome) to gate ticket creation
  const userMessageCount = useMemo(
    () => chatMessages.filter((m) => m.role === 'user').length,
    [chatMessages],
  );
  const canCreateTicket = userMessageCount >= 2;

  // Create ticket from chat conversation
  const handleCreateTicketFromChat = () => {
    const userMessages = chatMessages
      .filter((m) => m.role === 'user')
      .map((m) => m.content)
      .join('\n');
    const ticketSubject =
      userMessages.split('\n')[0]?.slice(0, 100) || 'Support request from AI chat';
    setSubject(ticketSubject);
    setDescription(userMessages);
    setDialogOpen(true);
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'default';
      case 'in_progress':
        return 'secondary';
      case 'resolved':
        return 'outline';
      case 'closed':
        return 'outline';
      default:
        return 'default';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Hero + Chat — central focus */}
      <div className="text-center space-y-4 pt-6">
        <h1 className="text-3xl font-bold">How can we help?</h1>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Chat with our AI assistant to get instant help. If you need further assistance, you can
          create a support ticket from the chat.
        </p>
      </div>

      <Card className="overflow-hidden transition-all duration-300">
        {!chatOpen ? (
          /* Collapsed: simple input */
          <CardContent className="p-4">
            <form onSubmit={handleHeroSubmit} className="flex gap-2">
              <Input
                placeholder="Ask a question..."
                value={heroInput}
                onChange={(e) => setHeroInput(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" size="icon" variant="secondary">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        ) : (
          /* Expanded: full chat */
          <div className="flex flex-col" style={{ height: '520px' }}>
            {/* Chat header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">AI Support Assistant</span>
              </div>
              <div className="flex items-center gap-1">
                {canCreateTicket && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleCreateTicketFromChat}
                    title="Create ticket from conversation"
                  >
                    <TicketPlus className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleResetChat}
                  disabled={isStreaming || chatMessages.length <= 1}
                  title="New chat"
                >
                  <SquarePen className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleCloseChat}
                  disabled={isStreaming}
                  title="Close chat"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
              {chatMessages.map((message) => {
                const isUser = message.role === 'user';
                return (
                  <div
                    key={message.id}
                    className={cn('flex gap-2.5', isUser ? 'justify-end' : 'justify-start')}
                  >
                    {!isUser && (
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-3.5 w-3.5 text-primary" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'max-w-[80%] rounded-xl px-3 py-2 text-sm',
                        isUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
                      )}
                    >
                      <div className="break-words">
                        {message.isStreaming && !message.content ? (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <div className="flex gap-0.5">
                              <span
                                className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                style={{ animationDelay: '0ms' }}
                              />
                              <span
                                className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                style={{ animationDelay: '150ms' }}
                              />
                              <span
                                className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                style={{ animationDelay: '300ms' }}
                              />
                            </div>
                            <span className="text-xs">Thinking...</span>
                          </div>
                        ) : !isUser ? (
                          (() => {
                            const { content: displayContent, isError } = formatMessageContent(
                              message.content,
                            );
                            return (
                              <div
                                className={cn(
                                  'prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ul:pl-4 prose-ol:my-1 prose-ol:pl-4 prose-li:my-0 prose-li:pl-0 prose-headings:my-1.5 prose-strong:text-foreground [&_ul_ul]:pl-3 [&_ol_ol]:pl-3',
                                  isError && 'text-amber-600 dark:text-amber-400',
                                )}
                              >
                                {isError ? (
                                  displayContent
                                ) : (
                                  <ReactMarkdown>{displayContent}</ReactMarkdown>
                                )}
                                {message.isStreaming && message.content && (
                                  <span className="inline-block w-1.5 h-3.5 bg-current ml-0.5 animate-pulse" />
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          message.content
                        )}
                      </div>
                    </div>
                    {isUser && (
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                        <User className="h-3.5 w-3.5 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Chat input */}
            <div className="border-t border-border px-3 py-2.5">
              <form onSubmit={handleChatSubmit} className="relative">
                <Textarea
                  ref={chatTextareaRef}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder="Ask a follow-up question..."
                  disabled={isStreaming}
                  className="min-h-[38px] max-h-[120px] pr-12 resize-none rounded-xl bg-muted/50 overflow-y-auto text-sm"
                  rows={1}
                  style={{ height: 'auto' }}
                />
                {isStreaming ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    onClick={handleStop}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg"
                  >
                    <Square className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!chatInput.trim()}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                )}
              </form>
            </div>
          </div>
        )}
      </Card>

      {/* Actions Row */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Link to={`${ARCHE_PATH}/faq`}>
            <Button variant="outline">
              <HelpCircle className="h-4 w-4 mr-2" />
              Browse FAQ
            </Button>
          </Link>
        </div>
        {isAdmin && (
          <Link to={`${ARCHE_PATH}/admin`}>
            <Button variant="outline">
              <Shield className="h-4 w-4 mr-2" />
              Admin Dashboard
            </Button>
          </Link>
        )}
      </div>

      {/* Ticket creation dialog — only reachable from chat after 2+ messages */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={handleCreateTicket}>
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
              <DialogDescription>
                Describe your issue and we will get back to you as soon as possible.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="Brief description of your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Provide details about your issue..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  required
                />
              </div>
            </div>
            {ticketError && <p className="text-sm text-destructive">{ticketError}</p>}
            <DialogFooter>
              <Button type="submit" disabled={submitting || !subject.trim() || !description.trim()}>
                {submitting ? 'Creating...' : 'Create Ticket'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Top FAQs */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Top FAQs
        </h2>
        {faqsLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        ) : topFaqs.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No FAQs available yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {topFaqs.map((faq) => (
              <Card key={faq.faqId}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <Link to={`${ARCHE_PATH}/faq`}>
          <Button variant="ghost" className="w-full">
            View all FAQs
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Recent Tickets */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Ticket className="h-5 w-5" />
          Recent Tickets
        </h2>
        {ticketsLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : recentTickets.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground text-sm">
                No tickets yet. Ask our Fias AI Assistant for help above, and if your issue is not
                resolved you will be prompted to create a support ticket to be reviewed by our
                staff.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {recentTickets.map((ticket) => (
              <Link
                key={ticket.ticketId}
                to={`${ARCHE_PATH}/tickets/${ticket.ticketId}`}
                className="block"
              >
                <Card className="hover:bg-muted/50 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="space-y-1 flex-1 min-w-0">
                      <p className="font-medium truncate">{ticket.subject}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant={statusColor(ticket.status)}>{ticket.status}</Badge>
                        <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
